import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import LogomarkPattern from '../components/LogomarkPattern';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bannerError, setBannerError] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/blog/posts/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data);
      } else if (response.status === 404) {
        setError('Post not found');
      } else {
        setError('Failed to load post');
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Custom components for markdown rendering
  const markdownComponents = {
    img: ({ node, ...props }) => {
      const src = props.src?.startsWith('/') ? `${API_URL}${props.src}` : props.src;
      return (
        <img
          {...props}
          src={src}
          alt={props.alt || ''}
          style={{
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '8px',
            margin: '24px 0'
          }}
        />
      );
    },
    h1: ({ children }) => (
      <h1 style={{
        fontFamily: 'Montserrat, sans-serif',
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 1.2,
        color: '#0a4447',
        marginTop: '48px',
        marginBottom: '24px'
      }}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 style={{
        fontFamily: 'Montserrat, sans-serif',
        fontWeight: 700,
        fontSize: '2rem',
        lineHeight: 1.3,
        color: '#0a4447',
        marginTop: '40px',
        marginBottom: '20px'
      }}>{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 style={{
        fontFamily: 'Montserrat, sans-serif',
        fontWeight: 700,
        fontSize: '1.5rem',
        lineHeight: 1.4,
        color: '#0a4447',
        marginTop: '32px',
        marginBottom: '16px'
      }}>{children}</h3>
    ),
    p: ({ children }) => (
      <p style={{
        fontFamily: 'Lato, sans-serif',
        fontSize: '1.125rem',
        lineHeight: 1.8,
        color: '#374151',
        marginBottom: '24px'
      }}>{children}</p>
    ),
    ul: ({ children }) => (
      <ul style={{
        fontFamily: 'Lato, sans-serif',
        fontSize: '1.125rem',
        lineHeight: 1.8,
        color: '#374151',
        marginBottom: '24px',
        paddingLeft: '24px'
      }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{
        fontFamily: 'Lato, sans-serif',
        fontSize: '1.125rem',
        lineHeight: 1.8,
        color: '#374151',
        marginBottom: '24px',
        paddingLeft: '24px'
      }}>{children}</ol>
    ),
    li: ({ children }) => (
      <li style={{ marginBottom: '8px' }}>{children}</li>
    ),
    blockquote: ({ children }) => (
      <blockquote style={{
        borderLeft: '4px solid #C9A227',
        paddingLeft: '24px',
        margin: '32px 0',
        fontStyle: 'italic',
        color: '#4a5568'
      }}>{children}</blockquote>
    ),
    a: ({ children, href }) => (
      <a
        href={href}
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        style={{
          color: '#0D7377',
          textDecoration: 'underline',
          textUnderlineOffset: '3px'
        }}
      >{children}</a>
    ),
    code: ({ inline, children }) => {
      if (inline) {
        return (
          <code style={{
            backgroundColor: '#e8f4f5',
            padding: '2px 6px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '0.9em',
            color: '#0D7377'
          }}>{children}</code>
        );
      }
      return (
        <pre style={{
          backgroundColor: '#0a4447',
          padding: '24px',
          borderRadius: '8px',
          overflowX: 'auto',
          margin: '24px 0'
        }}>
          <code style={{
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            color: '#e8f4f5'
          }}>{children}</code>
        </pre>
      );
    },
    table: ({ children }) => (
      <div style={{ overflowX: 'auto', marginBottom: '24px' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: 'Lato, sans-serif'
        }}>{children}</table>
      </div>
    ),
    th: ({ children }) => (
      <th style={{
        backgroundColor: '#e8f4f5',
        padding: '12px 16px',
        textAlign: 'left',
        fontWeight: 600,
        color: '#0a4447',
        borderBottom: '2px solid #0D7377'
      }}>{children}</th>
    ),
    td: ({ children }) => (
      <td style={{
        padding: '12px 16px',
        borderBottom: '1px solid rgba(13, 115, 119, 0.15)',
        color: '#374151'
      }}>{children}</td>
    ),
    hr: () => (
      <hr style={{
        border: 'none',
        borderTop: '2px solid rgba(13, 115, 119, 0.15)',
        margin: '40px 0'
      }} />
    ),
    strong: ({ children }) => (
      <strong style={{ fontWeight: 700, color: '#0a4447' }}>{children}</strong>
    )
  };

  if (loading) {
    return (
      <div style={{
        marginTop: '80px',
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff'
      }}>
        <p style={{
          fontFamily: 'Lato, sans-serif',
          fontSize: '1.125rem',
          color: '#0a4447'
        }}>Loading article...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={{
        marginTop: '80px',
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
        padding: '40px 20px'
      }}>
        <h1 style={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 700,
          fontSize: '2rem',
          color: '#0a4447',
          marginBottom: '16px'
        }}>Article Not Found</h1>
        <p style={{
          fontFamily: 'Lato, sans-serif',
          fontSize: '1.125rem',
          color: '#4a5568',
          marginBottom: '32px'
        }}>The article you're looking for doesn't exist or has been removed.</p>
        <Link
          to="/blog"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#0D7377',
            color: '#ffffff',
            padding: '14px 28px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontFamily: 'Lato, sans-serif',
            fontWeight: 600
          }}
        >
          <ArrowLeft size={18} /> Back to Blog
        </Link>
      </div>
    );
  }

  const bannerUrl = post.banner_image?.startsWith('/') 
    ? `${API_URL}${post.banner_image}` 
    : post.banner_image;

  // Check if banner image is valid (not errored)
  const showBanner = bannerUrl && !bannerError;

  return (
    <div style={{ marginTop: '80px' }}>
      <SEO 
        title={post.title}
        description={post.excerpt}
        keywords={`${post.category_name || 'Blog'}, Digital Transformation, Resolv Digital, ${post.title}`}
        canonical={`https://resolvdigital.com.au/blog/${post.slug}`}
      />
      
      {/* Hidden image to detect load errors */}
      {bannerUrl && !bannerError && (
        <img 
          src={bannerUrl} 
          alt="" 
          onError={() => setBannerError(true)}
          style={{ display: 'none' }}
        />
      )}
      
      {/* Hero / Banner Section */}
      <section style={{
        minHeight: '50vh',
        display: 'flex',
        alignItems: 'flex-end',
        backgroundColor: '#0a4447',
        backgroundImage: showBanner ? `url(${bannerUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        padding: '0'
      }}>
        {/* Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: showBanner ? 'rgba(10, 68, 71, 0.75)' : 'rgba(10, 68, 71, 1)',
          zIndex: 1
        }} />
        
        {!showBanner && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0
          }}>
            <LogomarkPattern opacity={0.05} density="sparse" tint="gold" />
          </div>
        )}

        <div style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          padding: '120px clamp(20px, 5vw, 40px) 60px'
        }}>
          <div style={{
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            {/* Back Link */}
            <Link
              to="/blog"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: '#b8d8da',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.95rem',
                textDecoration: 'none',
                marginBottom: '24px',
                transition: 'color 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = '#C9A227'}
              onMouseLeave={(e) => e.target.style.color = '#b8d8da'}
            >
              <ArrowLeft size={18} /> Back to Blog
            </Link>

            {/* Category */}
            {post.category_name && (
              <span style={{
                display: 'inline-block',
                backgroundColor: 'rgba(201, 162, 39, 0.2)',
                color: '#C9A227',
                padding: '6px 16px',
                borderRadius: '4px',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.8rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '20px'
              }}>
                <Tag size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                {post.category_name}
              </span>
            )}

            {/* Title */}
            <h1 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              lineHeight: 1.15,
              color: '#ffffff',
              marginBottom: '24px'
            }}>
              {post.title}
            </h1>

            {/* Meta */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
              flexWrap: 'wrap'
            }}>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#b8d8da',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.95rem'
              }}>
                <User size={16} /> {post.author}
              </span>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#b8d8da',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.95rem'
              }}>
                <Calendar size={16} /> {formatDate(post.created_at)}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article style={{
        padding: '80px clamp(20px, 5vw, 40px)',
        backgroundColor: '#ffffff'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          {/* Excerpt */}
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.375rem',
            lineHeight: 1.7,
            color: '#0a4447',
            fontWeight: 500,
            marginBottom: '48px',
            paddingBottom: '48px',
            borderBottom: '2px solid rgba(13, 115, 119, 0.15)'
          }}>
            {post.excerpt}
          </p>

          {/* Markdown Content */}
          <div className="blog-content" data-testid="blog-post-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
            >
              {post.content}
            </ReactMarkdown>
          </div>
        </div>
      </article>

      {/* CTA */}
      <CTASection />
    </div>
  );
};

export default BlogPost;
